'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { ResponsiveImagePreloadHints } from '@/app/components/media/app-image/responsive-image/preload';
import { type ResponsiveImageProps, type ResponsiveImageSource } from '@/app/components/media/app-image/responsive-image/types';
import { buildResponsiveImageSrcSet, getResponsiveImageFallbackUrl, getResponsiveImageKey, getResponsiveImageSourceKey, pickFallback, toEffectiveSources } from '@/app/components/media/app-image/responsive-image/utils';
import { TRANSPARENT_IMAGE_PLACEHOLDER_SRC } from '@/app/components/media/app-image/shared/image-placeholder';
import { isSvgSrc } from '@/app/components/media/app-image/shared/image-source';
import { getImageAttrs, getProjectImageKitDefaultTransforms, sanitizeImageAlt } from '@/app/components/media/app-image/shared/project-adapter';
import { useImageLoadState } from '@/app/components/media/app-image/shared/use-image-load-state';
import { ImageStyles, ImageWrapperStyles } from '@/app/components/media/app-image/styles';
import { cn } from '@/app/utils/cn';
const EMPTY_RESPONSIVE_IMAGE_SOURCES: ResponsiveImageSource[] = [];
const renderResponsiveSourceElement = (source: ResponsiveImageSource) => {
  const isSvg = isSvgSrc(source.src);
  const srcSet = isSvg ? source.src : buildResponsiveImageSrcSet(source.src, source.widths, source.params, {
    defaultTransforms: getProjectImageKitDefaultTransforms()
  });
  const sizes = isSvg ? undefined : source.sizes;
  return <source key={getResponsiveImageSourceKey(source)} srcSet={srcSet} sizes={sizes} media={source.media} />;
};
export const ResponsiveImage = t0 => {
  const $ = _c(70);
  const {
    mobileSrc,
    desktopSrc,
    sources: t1,
    alt,
    width,
    height,
    fill: t2,
    fetchPriority,
    preload: t3,
    loading: t4,
    decoding,
    fade: t5,
    hoverEffect,
    objectFit: t6,
    objectPosition: t7,
    placeholder: t8,
    root,
    preloadOffset: t9,
    className,
    imgClassName,
    style,
    imgStyle,
    imgRef,
    testId,
    onLoad,
    onError
  } = t0;
  const sources = t1 === undefined ? EMPTY_RESPONSIVE_IMAGE_SOURCES : t1;
  const fill = t2 === undefined ? false : t2;
  const preload = t3 === undefined ? false : t3;
  const loading = t4 === undefined ? "lazy" : t4;
  const fade = t5 === undefined ? true : t5;
  const objectFit = t6 === undefined ? "cover" : t6;
  const objectPosition = t7 === undefined ? "center" : t7;
  const placeholder = t8 === undefined ? false : t8;
  const preloadOffset = t9 === undefined ? "0px 0px 0px 0px" : t9;
  let t10;
  if ($[0] !== desktopSrc || $[1] !== mobileSrc || $[2] !== sources) {
    t10 = toEffectiveSources({
      mobileSrc,
      desktopSrc,
      sources
    });
    $[0] = desktopSrc;
    $[1] = mobileSrc;
    $[2] = sources;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  const effectiveSources = t10;
  let t11;
  if ($[4] !== effectiveSources) {
    t11 = getResponsiveImageKey(effectiveSources);
    $[4] = effectiveSources;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  const imageKey = t11;
  const t12 = !!fade;
  const t13 = !!placeholder;
  let t14;
  if ($[6] !== imageKey || $[7] !== loading || $[8] !== onLoad || $[9] !== preload || $[10] !== preloadOffset || $[11] !== root || $[12] !== t12 || $[13] !== t13) {
    t14 = {
      imageKey,
      fade: t12,
      placeholder: t13,
      preload,
      loading,
      root,
      preloadOffset,
      onLoad
    };
    $[6] = imageKey;
    $[7] = loading;
    $[8] = onLoad;
    $[9] = preload;
    $[10] = preloadOffset;
    $[11] = root;
    $[12] = t12;
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const {
    decodingAttr,
    handleLoad,
    isLoaded,
    loadingAttr,
    shouldAnimatePlaceholder,
    shouldFade,
    shouldLoadImage,
    shouldPreloadImage,
    shouldShowPlaceholder,
    wrapperRef
  } = useImageLoadState(t14);
  let t15;
  if ($[15] !== effectiveSources) {
    t15 = pickFallback(effectiveSources);
    $[15] = effectiveSources;
    $[16] = t15;
  } else {
    t15 = $[16];
  }
  const fallbackSource = t15;
  let t16;
  if ($[17] !== effectiveSources || $[18] !== shouldLoadImage) {
    t16 = shouldLoadImage ? effectiveSources.map(renderResponsiveSourceElement) : null;
    $[17] = effectiveSources;
    $[18] = shouldLoadImage;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const sourceNodes = t16;
  let t17;
  if ($[20] !== fallbackSource || $[21] !== shouldLoadImage) {
    t17 = shouldLoadImage ? getResponsiveImageFallbackUrl(fallbackSource, {
      defaultTransforms: getProjectImageKitDefaultTransforms()
    }) : "";
    $[20] = fallbackSource;
    $[21] = shouldLoadImage;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const fallbackImageUrl = t17;
  const renderedImageSrc = shouldLoadImage && fallbackImageUrl ? fallbackImageUrl : TRANSPARENT_IMAGE_PLACEHOLDER_SRC;
  let t18;
  if ($[23] !== className || $[24] !== fill || $[25] !== isLoaded || $[26] !== shouldAnimatePlaceholder || $[27] !== shouldShowPlaceholder) {
    t18 = cn(ImageWrapperStyles({
      fill,
      placeholder: shouldShowPlaceholder,
      animatePlaceholder: shouldAnimatePlaceholder,
      isLoaded: shouldShowPlaceholder && isLoaded
    }), className);
    $[23] = className;
    $[24] = fill;
    $[25] = isLoaded;
    $[26] = shouldAnimatePlaceholder;
    $[27] = shouldShowPlaceholder;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const wrapperClassName = t18;
  let t19;
  if ($[29] !== hoverEffect || $[30] !== imgClassName || $[31] !== isLoaded || $[32] !== objectFit || $[33] !== objectPosition || $[34] !== shouldFade) {
    t19 = cn(ImageStyles({
      fade: shouldFade,
      hoverEffect,
      objectFit,
      objectPosition
    }), {
      "opacity-100": isLoaded && shouldFade,
      "is-loaded": isLoaded
    }, imgClassName);
    $[29] = hoverEffect;
    $[30] = imgClassName;
    $[31] = isLoaded;
    $[32] = objectFit;
    $[33] = objectPosition;
    $[34] = shouldFade;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const imageClassName = t19;
  let t20;
  if ($[36] !== effectiveSources || $[37] !== fetchPriority || $[38] !== shouldPreloadImage) {
    t20 = shouldPreloadImage ? <ResponsiveImagePreloadHints fetchPriority={fetchPriority} sources={effectiveSources} /> : null;
    $[36] = effectiveSources;
    $[37] = fetchPriority;
    $[38] = shouldPreloadImage;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const preloadedSources = t20;
  if (effectiveSources.length === 0) {
    return null;
  }
  const t21 = testId ?? "responsive-image";
  let t22;
  if ($[40] !== t21) {
    t22 = getImageAttrs({
      testid: t21
    });
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = decoding ?? decodingAttr;
  let t24;
  if ($[42] !== alt) {
    t24 = sanitizeImageAlt(alt);
    $[42] = alt;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  const t25 = fill ? undefined : width;
  const t26 = fill ? undefined : height;
  let t27;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = getImageAttrs({
      testid: "image"
    });
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== fetchPriority || $[46] !== handleLoad || $[47] !== imageClassName || $[48] !== imageKey || $[49] !== imgRef || $[50] !== imgStyle || $[51] !== loadingAttr || $[52] !== onError || $[53] !== renderedImageSrc || $[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t26 || $[58] !== t27) {
    t28 = <img key={imageKey} ref={imgRef} className={imageClassName} style={imgStyle} decoding={t23} fetchPriority={fetchPriority} loading={loadingAttr} src={renderedImageSrc} alt={t24} width={t25} height={t26} onLoad={handleLoad} onError={onError} {...t27} />;
    $[45] = fetchPriority;
    $[46] = handleLoad;
    $[47] = imageClassName;
    $[48] = imageKey;
    $[49] = imgRef;
    $[50] = imgStyle;
    $[51] = loadingAttr;
    $[52] = onError;
    $[53] = renderedImageSrc;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== sourceNodes || $[61] !== style || $[62] !== t22 || $[63] !== t28 || $[64] !== wrapperClassName || $[65] !== wrapperRef) {
    t29 = <picture ref={wrapperRef} className={wrapperClassName} style={style} {...t22}>{sourceNodes}{t28}</picture>;
    $[60] = sourceNodes;
    $[61] = style;
    $[62] = t22;
    $[63] = t28;
    $[64] = wrapperClassName;
    $[65] = wrapperRef;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== preloadedSources || $[68] !== t29) {
    t30 = <>{preloadedSources}{t29}</>;
    $[67] = preloadedSources;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  return t30;
};